// SPDX-License-Identifier: AGPL-3.0-or-later

const APP_SHELL_CACHE_KEY = '/app-shell';
const APP_SHELL_SEED_PATH = '/app';
const APP_SHELL_MARKER_HEADER = 'x-fluxer-app-shell';

export interface PrecacheEntry {
	readonly url: string;
	readonly revision: string;
}

export interface AppShellRuntime {
	readonly caches: {open(name: string): Promise<Pick<Cache, 'match' | 'put'>>} | undefined;
	readonly fetch: (request: Request) => Promise<Response>;
	readonly origin: string;
	readonly precacheName: string;
	readonly navigationCacheName: string;
	readonly networkTimeoutMs: number;
	readonly onCacheWriteError: (error: unknown) => void;
}

export function isCacheableResponse(response: Response): boolean {
	return response.ok || response.type === 'opaque';
}

export async function precacheAssets(runtime: AppShellRuntime, manifest: ReadonlyArray<PrecacheEntry>): Promise<void> {
	if (!runtime.caches) {
		return;
	}
	const cache = await runtime.caches.open(runtime.precacheName);
	await Promise.allSettled(
		manifest.map(async (entry) => {
			const request = new Request(new URL(entry.url, runtime.origin).toString(), {cache: 'reload'});
			const response = await runtime.fetch(request);
			if (isCacheableResponse(response)) {
				await cache.put(entry.url, response);
			}
		}),
	);
}

async function readAppShell(runtime: AppShellRuntime): Promise<Response | undefined> {
	if (!runtime.caches) {
		return undefined;
	}
	const cache = await runtime.caches.open(runtime.navigationCacheName);
	return (await cache.match(APP_SHELL_CACHE_KEY)) ?? undefined;
}

async function storeAppShell(runtime: AppShellRuntime, response: Response): Promise<void> {
	if (!runtime.caches || !response.ok || response.headers.get(APP_SHELL_MARKER_HEADER) !== '1') {
		return;
	}
	try {
		const cache = await runtime.caches.open(runtime.navigationCacheName);
		await cache.put(APP_SHELL_CACHE_KEY, response.clone());
	} catch (error) {
		runtime.onCacheWriteError(error);
	}
}

export async function seedAppShell(runtime: AppShellRuntime): Promise<void> {
	if (!runtime.caches) {
		return;
	}
	const request = new Request(new URL(APP_SHELL_SEED_PATH, runtime.origin).toString(), {cache: 'reload'});
	const response = await runtime.fetch(request);
	await storeAppShell(runtime, response);
}

export async function fetchAppShellNavigation(runtime: AppShellRuntime, request: Request): Promise<Response> {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	const timeout = new Promise<Response | undefined>((resolve) => {
		timeoutId = setTimeout(() => resolve(undefined), runtime.networkTimeoutMs);
	});
	try {
		const network = runtime.fetch(request).then(async (response) => {
			await storeAppShell(runtime, response);
			return response;
		});
		let networkResponse: Response | undefined;
		try {
			networkResponse = await Promise.race([network, timeout]);
			if (networkResponse && isCacheableResponse(networkResponse)) {
				return networkResponse;
			}
		} catch {}
		const cached = await readAppShell(runtime);
		if (cached) {
			network.catch(() => undefined);
			return cached;
		}
		if (networkResponse) {
			return networkResponse;
		}
		return network;
	} finally {
		clearTimeout(timeoutId);
	}
}
